import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { BreadcrumbService, ModalService, SiteService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import {
  AP_ADVANCE_REQUEST_ENTITY_CODE,
  AP_PAYMENT_REQUEST_ENTITY_CODE,
  CashRequest,
  CashRequestBreakdownEntry,
  CashRequestForm,
  CashRequestHeaderUpdate,
  CashRequestInsert,
  CashRequestLine,
  CashRequestStatus,
  CashRequestUsage,
  CashRequestUsageLine,
  CashRequestUsageSourceType,
  Currency,
} from '../../../models/cash-request/cash-request';
import { ApPaymentStatus } from '../../../models/ap-payment/ap-payment';
import { CashRequestProxyService } from '../../../proxies/cash-request.proxy.service';
import { SiteCompanyService } from '../../../services/site-company.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ApDocumentQuickViewModalComponent } from '../../shared/modals/ap-document-quick-view-modal/ap-document-quick-view-modal.component';
import { CashRequestDocumentPickerModalComponent } from '../_modals/cash-request-document-picker-modal/cash-request-document-picker-modal.component';
import { CashRequestLineModalComponent } from '../_modals/cash-request-line-modal/cash-request-line-modal.component';

/**
 * Cash Request details — create a draft and plan documents against it.
 *
 * <p>A line IS a GL account (#4863): everything planned against that account for the week lives in its
 * breakdown, and the line's amount is the sum of those entries — never typed. Lines are therefore not created
 * by hand here; the document picker routes each payment request or withholding to a line by its own AP
 * control account, creating the line when the account is new to the request. A new request is created empty
 * and filled from the picker.</p>
 *
 * <p>Manual (documentless) entries — planillas and statutory obligations — are the other half of a
 * breakdown, and #5135 gives them their way in: "Add line" opens an account nothing has been planned
 * against yet, and the pencil on an existing line adds to one documents already put there. Both go through
 * the same modal, and neither can disturb what the picker planned.</p>
 */
@Component({
  selector: 'app-cash-request-details',
  templateUrl: './cash-request-details.component.html',
  styleUrl: './cash-request-details.component.scss',
})
export class CashRequestDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(CashRequestProxyService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  private companyRef: string | null = null;
  /** Per-line description edit controls for an existing Draft (keyed by lineId). */
  private readonly lineDescControls = new Map<string, FormControl<string | null>>();

  readonly isLoading = signal(0);
  readonly isNew = signal(true);
  /** Guards the export button while the workbook is being rendered (#5944). */
  readonly isExporting = signal(false);
  readonly currentId = signal<string | null>(null);
  readonly headerTitle = signal<string>('');

  readonly status = signal<CashRequestStatus>(CashRequestStatus.Draft);
  readonly request = signal<CashRequest | null>(null);

  /** Lines start expanded — the breakdown is what the page is for. A plain object, not a signal: p-table
   *  takes `expandedRowKeys` as an input it mutates in place and emits nothing back. */
  expandedRows: Record<string, boolean> = {};

  readonly isDraft = computed(() => this.status() === CashRequestStatus.Draft);
  readonly isSubmitted = computed(() => this.status() === CashRequestStatus.Submitted);
  readonly lines = computed<CashRequestLine[]>(() => this.request()?.lines ?? []);
  readonly hasLines = computed(() => this.lines().length > 0);

  // ── Usage (#4796) ─────────────────────────────────────────────────────────────

  /** What has been spent against the request. Loaded separately: it summarises AP payments, not the request. */
  readonly usage = signal<CashRequestUsage | null>(null);
  readonly usageLines = computed<CashRequestUsageLine[]>(() => this.usage()?.lines ?? []);

  /** Bar width. Clamped to 100 so an overdrawn week fills it rather than overflowing — the overdraw itself is
   *  reported by the figures beside it, which are not clamped. */
  readonly usageBarPercent = computed(() => Math.min(100, Math.max(0, this.usage()?.appliedPercent ?? 0)));

  /** More has been paid against the week than it planned for. A real outcome, reported rather than hidden. */
  readonly isOverdrawn = computed(() => (this.usage()?.remainingAmount ?? 0) < 0);

  /** By how much, as a positive number to read beside an "over plan" label. */
  readonly overdrawnAmount = computed(() => Math.abs(Math.min(0, this.usage()?.remainingAmount ?? 0)));

  /** A cash request is planned in USD — every entry contributes its functional amount. Not a choice. */
  readonly currency = Currency.USD;

  /** Everything except Monday (getDay(): 0 = Sunday … 6 = Saturday) — the calendar offers Mondays only. */
  readonly nonMondays = [0, 2, 3, 4, 5, 6];

  readonly form: FormGroup<CashRequestForm> = this.formBuilder.group<CashRequestForm>({
    weekStart: new FormControl<Date | null>(this.toMonday(new Date()), [Validators.required]),
    notes: new FormControl<string | null>(null),
  });

  /**
   * The Monday that starts the week containing `date`.
   *
   * <p>The calendar only offers Mondays, so this is the invariant behind that rather than the mechanism: it
   * keeps "the stored week start is a Monday" true for values that do not come from a click — the initial
   * default, and anything a future entry point sets.</p>
   *
   * <p>Sunday counts as the END of the week that started six days earlier, matching WeekEnd = WeekStart + 6.
   * Reading Sunday as the start of a new week would file its documents against the wrong week.</p>
   */
  private toMonday(date: Date): Date {
    const monday = new Date(date);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    return monday;
  }

  /** The Sunday the picked week ends on — derived, and what the API stores when WeekEnd is omitted. */
  weekEnd(): Date | null {
    const start = this.form.controls.weekStart.value;
    if (!start) return null;
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return end;
  }

  ngOnInit(): void {
    // Whatever day is picked, the value stored is that week's Monday.
    this.form.controls.weekStart.valueChanges.subscribe((value) => {
      if (!value) return;
      const monday = this.toMonday(value);
      if (monday.getTime() !== new Date(value).setHours(0, 0, 0, 0)) {
        this.form.controls.weekStart.setValue(monday, { emitEvent: false });
      }
    });

    this.startLoading();
    this.siteCompany
      .getFinancialCompanyId(SiteService.getSelectedSiteId())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (id) => (this.companyRef = id),
        error: () => (this.companyRef = null),
      });

    // Subscribed, not read off the snapshot: creating a draft navigates from `?id=` to `?id=<new id>` on
    // the same route, which reuses this component. On a snapshot read ngOnInit never fires again, which is
    // why that path used to force a full `location.reload()` to show the request it had just created.
    this.route.queryParamMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.isNew.set(false);
        this.currentId.set(id);
        this.load(id);
      } else {
        this.isNew.set(true);
        this.currentId.set(null);
        this.request.set(null);
        this.status.set(CashRequestStatus.Draft);
      }
      this.applyHeader();
    });
  }

  /** Downloads the request as a flat workbook, one row per breakdown entry (#5944). */
  exportExcel(): void {
    const id = this.currentId();
    if (!id || this.isExporting()) return;

    this.isExporting.set(true);
    this.service
      .export(id)
      .pipe(finalize(() => this.isExporting.set(false)))
      // Failures are toasted by the shared errorInterceptor, which reads the API's own message out of the blob.
      .subscribe((blob) => this.triggerDownload(blob, `cash-request_${this.request()?.number ?? id}.xlsx`));
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  /** Header title + breadcrumb tail. The number only exists once the draft is created. */
  private applyHeader(): void {
    const title = this.isNew() ? this.t('CashRequest_New', 'New cash request') : (this.request()?.number ?? this.t('Details', 'Details'));
    this.headerTitle.set(title);
    this.breadcrumbService.setCurrent(this.isNew() ? 'Common_New' : this.request()?.number);
  }

  private load(id: string): void {
    this.startLoading();
    this.service
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) this.applyRequest(response.data);
        },
        error: () => this.request.set(null),
      });
  }

  /**
   * Loads what has been spent against the request. Failure is swallowed on purpose: usage is a report ABOUT
   * the request, and a page that refuses to render the plan because its consumption could not be summarised
   * would be worse than one showing no usage.
   */
  private loadUsage(id: string): void {
    this.service.getUsage(id).subscribe({
      next: (response) => this.usage.set(response.data ?? null),
      error: () => this.usage.set(null),
    });
  }

  /** Sets the loaded request and rebuilds the editable header form + per-line description controls. */
  private applyRequest(data: CashRequest): void {
    this.request.set(data);
    this.status.set(data.status);
    this.applyHeader();
    this.form.patchValue(
      {
        weekStart: data.weekStart ? new Date(data.weekStart) : null,
        notes: data.notes ?? null,
      },
      // The stored value is already a Monday; re-snapping it would only fight the loaded state.
      { emitEvent: false },
    );
    this.lineDescControls.clear();
    const expanded: Record<string, boolean> = {};
    for (const line of data.lines ?? []) {
      if (!line.lineId) continue;
      this.lineDescControls.set(line.lineId, new FormControl<string | null>(line.description ?? null));
      expanded[line.lineId] = true;
    }
    this.expandedRows = expanded;

    // Refreshed alongside the request rather than only on first load: planning a document changes the PLAN,
    // which is the denominator every usage figure is measured against, so a stale summary would report the
    // wrong percentage the moment the picker closes.
    if (data.id) this.loadUsage(data.id);
  }

  /** Description edit control for an existing Draft line (created lazily if missing). */
  descControl(lineId?: string): FormControl<string | null> {
    if (!lineId) return new FormControl<string | null>(null);
    let control = this.lineDescControls.get(lineId);
    if (!control) {
      control = new FormControl<string | null>(null);
      this.lineDescControls.set(lineId, control);
    }
    return control;
  }

  /** Save a Draft's header (week, notes — currency is locked after creation) + per-line description edits. */
  saveDraft(): void {
    const id = this.currentId();
    const req = this.request();
    if (!id || !req) return;
    const v = this.form.getRawValue();
    if (!v.weekStart) {
      GlobalMessageUtil.warn(this.t('CashRequest', 'Cash Request'), this.t('CashRequest_WeekRequired', 'A week start is required.'));
      return;
    }
    const update: CashRequestHeaderUpdate = {
      currency: req.currency, // locked after creation — send the existing value
      weekStart: v.weekStart.toISOString(),
      weekEnd: null, // derived server-side as WeekStart + 6
      notes: v.notes ?? null,
      lineDescriptions: (req.lines ?? [])
        .filter((l) => l.lineId)
        .map((l) => ({ lineId: l.lineId!, description: this.descControl(l.lineId).value })),
    };
    this.startLoading();
    this.service
      .updateHeader(id, update)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.applyRequest(response.data);
            GlobalMessageUtil.success(this.t('CashRequest', 'Cash Request'), this.t('CashRequest_HeaderSaved', 'Changes saved.'));
          }
        },
      });
  }

  // ── Display helpers ───────────────────────────────────────────────────────────

  /** Currency label shown on the header. USD for a new request; an existing one shows what it was created as. */
  currencyLabel(): string {
    const currency = this.isNew() ? this.currency : this.request()?.currency;
    return currency != null ? (Currency[currency] ?? 'USD') : 'USD';
  }

  currencyCode(currency: Currency | null | undefined): string {
    return currency != null ? (Currency[currency] ?? '') : '';
  }

  /** True when the entry came from the picker rather than being typed. */
  isDocumentBacked(entry: CashRequestBreakdownEntry): boolean {
    return !!entry.sourceDocument?.reference;
  }

  /** What an entry is: the document's number, or the requester's own text for a manual one. */
  entryLabel(entry: CashRequestBreakdownEntry): string {
    return entry.sourceDocument?.number || entry.description || this.t('CashRequest_ManualEntry', 'Manual entry');
  }

  /**
   * Opens the source document in a NEW TAB — a cash request is being assembled, and navigating away in place
   * would abandon that work to go and read one of its inputs.
   */
  openSourceDocument(entry: CashRequestBreakdownEntry): void {
    const source = entry.sourceDocument;
    if (!source?.reference) return;
    const path =
      source.entityDefinitionCode === AP_PAYMENT_REQUEST_ENTITY_CODE
        ? '/ap-payment-request-details'
        : source.entityDefinitionCode === AP_ADVANCE_REQUEST_ENTITY_CODE
          ? '/ap-advance-request-details'
          : '/ap-withholding-details';
    const url = this.router.serializeUrl(this.router.createUrlTree([path], { queryParams: { id: source.reference } }));
    window.open(url, '_blank');
  }

  // ── Usage display (#4796) ─────────────────────────────────────────────────────

  /** Where the week stands: nothing drawn, part of it, all of it, or past it. */
  usageStateLabel(): string {
    const usage = this.usage();
    if (!usage || usage.appliedAmount === 0) return this.t('CashRequest_Usage_Unused', 'Not used yet');
    if (usage.remainingAmount < 0) return this.t('CashRequest_Usage_Over', 'Over plan');
    if (usage.remainingAmount === 0) return this.t('CashRequest_Usage_Full', 'Fully used');
    return this.t('CashRequest_Usage_Partial', 'Partially used');
  }

  usageStateSeverity(): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    const usage = this.usage();
    if (!usage || usage.appliedAmount === 0) return 'secondary';
    if (usage.remainingAmount < 0) return 'danger';
    if (usage.remainingAmount === 0) return 'success';
    return 'info';
  }

  /** "Banco Ficohsa · HNL · ****8830" — the bank part is dropped on a draft with no account chosen yet. */
  bankLabel(line: CashRequestUsageLine): string {
    const parts = [line.bankName, this.currencyCode(line.currency)].filter((p): p is string => !!p);
    const last4 = line.bankAccountNumber?.slice(-4);
    if (last4) parts.push(`****${last4}`);
    return parts.join(' · ');
  }

  /** The USD figure is worth showing only when it is not the same number already in the amount column. */
  showsFunctional(line: CashRequestUsageLine): boolean {
    return line.currency !== Currency.USD;
  }

  paymentStatusLabel(status: ApPaymentStatus): string {
    return ApPaymentStatus[status] ?? '';
  }

  paymentStatusSeverity(status: ApPaymentStatus): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    return status === ApPaymentStatus.Posted ? 'success' : 'secondary';
  }

  isRemittance(line: CashRequestUsageLine): boolean {
    return line.sourceType === CashRequestUsageSourceType.WithholdingRemittance;
  }

  /**
   * Opens the paying document in a NEW TAB — same reason as the source documents: the request is mid-assembly.
   *
   * <p>A remittance has no per-id page (it is addressed by period), so it opens on its own month rather than
   * on whatever month that screen would default to.</p>
   */
  openPayment(line: CashRequestUsageLine): void {
    if (!line.sourceReference) return;
    const tree = this.isRemittance(line)
      ? this.router.createUrlTree(['/withholding-remittance'], { queryParams: this.remittancePeriod(line.date) })
      : this.router.createUrlTree(['/ap-payment-details'], { queryParams: { id: line.sourceReference } });
    window.open(this.router.serializeUrl(tree), '_blank');
  }

  /** A remittance's date is its DUE date — the 10th of the month AFTER the period it settles — so the screen
   *  has to be sent one month back, or it would open on the month after the one being looked at. */
  private remittancePeriod(dueDate: string): { year: number; month: number } {
    const period = new Date(dueDate);
    period.setMonth(period.getMonth() - 1);
    return { year: period.getFullYear(), month: period.getMonth() + 1 };
  }

  /** The invoice itself opens in a modal (#4866) rather than a tab — it is a glance, not a destination. */
  openDocumentQuickView(documentReference: string | null | undefined): void {
    if (documentReference)
      this.modalService
        .open(ApDocumentQuickViewModalComponent, { modalClass: 'modal_middle' }, { documentId: documentReference })
        .subscribe();
  }

  statusLabel(status: number): string {
    return CashRequestStatus[status] ?? '';
  }

  statusSeverity(status: number): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    switch (status) {
      case CashRequestStatus.Approved:
        return 'success';
      case CashRequestStatus.Submitted:
        return 'info';
      case CashRequestStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  // ── Create (new) ──────────────────────────────────────────────────────────────

  /**
   * Creates the draft with a header and NO lines. Lines are an outcome of planning documents, not something
   * typed up front, so the picker on the saved draft is the way in.
   */
  createDraft(): void {
    if (!this.companyRef) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      GlobalMessageUtil.warn(this.t('CashRequest', 'Cash Request'), this.t('CashRequest_Invalid', 'A week start is required.'));
      return;
    }
    const v = this.form.getRawValue();
    const insert: CashRequestInsert = {
      financialCompanyReference: this.companyRef,
      currency: this.currency,
      weekStart: (v.weekStart ?? this.toMonday(new Date())).toISOString(),
      weekEnd: null, // derived server-side as WeekStart + 6
      notes: v.notes ?? null,
      lines: [],
    };
    this.startLoading();
    this.service
      .create(insert)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const saved = response.data;
          if (response.success && saved?.id) {
            GlobalMessageUtil.success(this.t('CashRequest', 'Cash Request'), this.t('CashRequest_Created', 'Draft created.'));
            // The queryParamMap subscription above picks this up and loads the saved draft in place.
            this.router.navigate(['/cash-request-details'], { queryParams: { id: saved.id } });
          }
        },
      });
  }

  // ── Planning documents (#4864) ────────────────────────────────────────────────

  /** Opens the picker. It commits its own delta and hands back the updated request. */
  openPicker(): void {
    const id = this.currentId();
    if (!id) return;
    this.modalService
      .open(CashRequestDocumentPickerModalComponent, { modalClass: 'modal_extralarge' }, { cashRequestId: id })
      .subscribe((result) => {
        if (result?.action === 'Save' && result.data) this.applyRequest(result.data as CashRequest);
      });
  }

  /**
   * Opens the manual line editor (#5135) — with a line to add entries to it, without one to open a brand
   * new account on the request.
   *
   * <p>The modal commits its own save and hands back the updated request, exactly as the picker does, so
   * this page never has to know which of the two endpoints was the right one for the case.</p>
   */
  openLineModal(line?: CashRequestLine | null): void {
    const id = this.currentId();
    if (!id) return;
    this.modalService
      .open(CashRequestLineModalComponent, { modalClass: 'modal_large' }, { cashRequestId: id, line: line ?? null })
      .subscribe((result) => {
        if (result?.action === 'Save' && result.data) this.applyRequest(result.data as CashRequest);
      });
  }

  /**
   * Unplans one document from the request. Removing the last entry on a line takes the line with it — an
   * account nobody planned anything against is not a line.
   */
  unplanEntry(entry: CashRequestBreakdownEntry): void {
    const id = this.currentId();
    const source = entry.sourceDocument;
    if (!id || !source?.reference || source.entityDefinitionCode == null) return;

    this.confirmationService.confirm({
      header: this.t('CashRequest_Unplan', 'Remove document'),
      message: this.t('CashRequest_Unplan_Msg', 'Remove this document from the request?'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.startLoading();
        this.service
          .updateDocuments(id, { add: [], remove: [{ reference: source.reference!, entityDefinitionCode: source.entityDefinitionCode! }] })
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: (response) => {
              if (response.success && response.data) this.applyRequest(response.data);
            },
          });
      },
    });
  }

  removeExistingLine(lineId: string): void {
    const id = this.currentId();
    if (!id || !lineId) return;
    this.confirmationService.confirm({
      header: this.t('CashRequest_RemoveLine', 'Remove line'),
      message: this.t('CashRequest_RemoveLine_Msg', 'Remove this account and everything planned against it?'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.startLoading();
        this.service
          .removeLine(id, lineId)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: (response) => {
              if (response.success && response.data) this.applyRequest(response.data);
            },
          });
      },
    });
  }

  // ── Per-line attachment (save the line first) ─────────────────────────────────
  onLineFile(event: Event, lineId: string | undefined, input: HTMLInputElement): void {
    const id = this.currentId();
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!id || !lineId || !file) return;
    this.startLoading();
    this.service
      .uploadLineAttachment(id, lineId, file)
      .pipe(
        finalize(() => {
          this.stopLoading();
          input.value = '';
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.applyRequest(response.data);
            GlobalMessageUtil.success(this.t('CashRequest', 'Cash Request'), this.t('CashRequest_Attached', 'Attachment uploaded.'));
          }
        },
      });
  }

  openAttachment(url: string | null | undefined): void {
    if (url) window.open(url, '_blank');
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────────
  submit(): void {
    this.lifecycle(
      'submit',
      this.t('CashRequest_Submit', 'Submit request'),
      this.t('CashRequest_Submit_Msg', 'Submit this request for approval? Lines cannot be edited afterwards.'),
    );
  }

  approve(): void {
    this.lifecycle(
      'approve',
      this.t('CashRequest_Approve', 'Approve request'),
      this.t('CashRequest_Approve_Msg', 'Approve this request? It then becomes available to fund.'),
    );
  }

  cancel(): void {
    this.lifecycle('cancel', this.t('CashRequest_Cancel', 'Cancel request'), this.t('CashRequest_Cancel_Msg', 'Cancel this request?'));
  }

  private lifecycle(action: 'submit' | 'approve' | 'cancel', header: string, message: string): void {
    const id = this.currentId();
    if (!id) return;
    this.confirmationService.confirm({
      header,
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: action === 'cancel' ? 'p-button-danger' : 'p-button-success',
      accept: () => {
        this.startLoading();
        const op$ =
          action === 'submit' ? this.service.submit(id) : action === 'approve' ? this.service.approve(id) : this.service.cancel(id);
        op$.pipe(finalize(() => this.stopLoading())).subscribe({
          next: (response) => {
            if (response.success && response.data) {
              this.applyRequest(response.data);
              GlobalMessageUtil.success(this.t('CashRequest', 'Cash Request'), this.t('CashRequest_Updated', 'Request updated.'));
            }
          },
        });
      },
    });
  }

  back(): void {
    this.router.navigate(['/cash-requests']);
  }

  /**
   * Deletes this draft, after confirming (#5223).
   *
   * <p>Offered only on a SAVED draft: there is nothing to delete before the first save. The API applies the
   * same draft-only rule, so a record that moved on in another tab is answered by that refusal rather than
   * by whatever this screen last painted — the button is a convenience, not the guard.</p>
   *
   * <p>Hard delete: a draft has posted nothing, so there is no ledger entry to keep and nothing a surviving
   * row would explain. Hence the explicit warning that it cannot be undone.</p>
   */
  confirmDelete(): void {
    const id = this.currentId();
    if (!id || !this.isDraft()) return;
    this.confirmationService.confirm({
      header: this.t('CashRequest_Delete_Title', 'Delete draft'),
      message: this.t('CashRequest_Delete_Confirm', 'Delete this draft cash request? This cannot be undone.'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('Delete', 'Delete'),
      rejectLabel: this.t('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteDraft(id),
    });
  }

  private deleteDraft(id: string): void {
    this.service.delete(id).subscribe({
      next: () => {
        GlobalMessageUtil.success(
          this.t('CashRequest_Delete_Done_Title', 'Draft deleted'),
          this.t('CashRequest_Delete_Done', 'The draft was deleted.'),
        );
        this.router.navigate(['/cash-requests']);
      },
    });
  }
}
